import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  stockMovementsApi,
  type CreateStockMovementRequest,
  type ApprovalRequest,
  type IssueStockRequest,
} from '../services/stockMovementsApi';

export const useRecordMovement = () => {
  return useMutation({
    mutationFn: async (data: CreateStockMovementRequest & { type: 'receipt' | 'issue' | 'return' | 'adjustment' }) => {
      const { type, ...payload } = data;
      
      switch (type) {
        case 'receipt':
          return stockMovementsApi.recordReceipt(payload);
        case 'issue':
          return stockMovementsApi.recordIssue(payload);
        case 'return':
          return stockMovementsApi.recordReturn(payload);
        case 'adjustment':
          return stockMovementsApi.recordAdjustment(payload);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['movements', variables.inputId] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
};

export const useMovementsByInput = (inputId: string, startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ['movements', inputId, startDate, endDate],
    queryFn: () => stockMovementsApi.getMovementsByInput(inputId, startDate, endDate),
    enabled: !!inputId,
  });
};

export const usePendingApprovals = () => {
  return useQuery({
    queryKey: ['pending-approvals'],
    queryFn: stockMovementsApi.getPendingApprovals,
  });
};

export const useApproveMovement = () => {
  return useMutation({
    mutationFn: (payload: ApprovalRequest) => stockMovementsApi.approveMovement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
};

export const useIssueStock = () => {
  return useMutation({
    mutationFn: (payload: IssueStockRequest) =>
      stockMovementsApi.issueStockForRequisition(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['sowingInstructions'] });
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'sowing-requisition' || q.queryKey[0] === 'sowing-requisitions' });
    },
  });
};